import { Injectable, signal } from '@angular/core';
import { INITIAL_BICYCLE_ADAPTED_VALUE, INITIAL_FAST_VALUE, INITIAL_FLAT_VALUE, allOption } from '../constants';
import { FAST_OPTION, ITINERARY_OPTIONS } from '../constants/itinerary-options.constants';
import { MAX_WALK_TIME_OPTIONS } from '../constants/max-walk-time-options.constant';
import { NORMAL_SPEED, WALK_SPEED_OPTIONS } from '../constants/walk-speed.constant';
import { MoreOptionsRequestParameters, allOptionsRequestParameters, bikeOptionsRequestParameters, busOptionsRequestParameters, otpMoreOptionsRequestParameters, walkOptionsRequestParameters } from '@api/models/otpRouteRequest.model';
import { BICYCLE_NORMAL_SPEED, BICYCLE_SPEED_OPTIONS } from '../constants/bicycle-speed.constant copy';
import { Subject } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class RouteFormMoreOptionsService {

  public moreOptionsSelected = signal(allOption);

  public fast = signal(INITIAL_FAST_VALUE);
  public flat = signal(INITIAL_FLAT_VALUE);
  public bicycleAdapted = signal(INITIAL_BICYCLE_ADAPTED_VALUE);
  public resetSliderSubject = new Subject<boolean>
  
  public itineraryType = signal(FAST_OPTION);
  public maxWalkTimeOnBusItinerary = signal(MAX_WALK_TIME_OPTIONS[1]);
  public walkSpeed = signal(NORMAL_SPEED);
  public bikeSpeed = signal(BICYCLE_NORMAL_SPEED);

  public maxWalkTime = signal(MAX_WALK_TIME_OPTIONS[1]);
  public wheelcharAccessible = signal(false);
  public maxWalkTimeOnMultimodal = signal(MAX_WALK_TIME_OPTIONS[1]);

  public get resetObservable() {
    return this.resetSliderSubject.asObservable();
  }


  public resetOptions() {
    this.moreOptionsSelected.set(allOption);

    this.fast.set(INITIAL_FAST_VALUE);
    this.flat.set(INITIAL_FLAT_VALUE);
    this.bicycleAdapted.set(INITIAL_BICYCLE_ADAPTED_VALUE);

    this.itineraryType.set(FAST_OPTION);
    this.maxWalkTimeOnBusItinerary.set(MAX_WALK_TIME_OPTIONS[1]);
    this.walkSpeed.set(NORMAL_SPEED);
    this.bikeSpeed.set(BICYCLE_NORMAL_SPEED);
    
    this.maxWalkTime.set(MAX_WALK_TIME_OPTIONS[1]);
    this.wheelcharAccessible.set(false);
    this.maxWalkTimeOnMultimodal.set(MAX_WALK_TIME_OPTIONS[1]);

    this.resetSliderSubject.next(true);
  }

  public getDataForRequest(): MoreOptionsRequestParameters {
    return {
      walk: { ...this.getWalkDataForRequest() },
      bus: { ...this.getBusDataForRequest() },
      bicycle: { ...this.getBicycleDataForRequest() },
      all: { ...this.getMultimodalDataForRequest() }
    }
  }

  public setMoreOptionsFromUrlParams(params: MoreOptionsRequestParameters) {
    this.setWalkDataFromUrlParams(params.walk);
    this.setBusDataFromUrlParams(params.bus);
    this.setBicycleDataFromUrlParams(params.bicycle);
    this.setMultimodalDataFromUrlParams(params.all);
  }

  public getWalkDataForRequest() {
    return {
      wheelchair: this.wheelcharAccessible(),
      walkSpeed: this.walkSpeed().speed,
      maxWalkTime: this.maxWalkTime(),
      maxWalkDistance: Math.round(this.speedAndTimeToMetersDistance(this.maxWalkTime(), this.walkSpeed().speed)),
    }
  }

  private setWalkDataFromUrlParams(params: walkOptionsRequestParameters) {
    this.maxWalkTime.set(params.maxWalkTime);
    this.walkSpeed.set(WALK_SPEED_OPTIONS.find((option) => option.speed === params.walkSpeed) ?? NORMAL_SPEED);
    this.wheelcharAccessible.set(params.wheelchair);
  }

  public getBusDataForRequest() {
    return {
      walkSpeed: this.walkSpeed().speed,
      maxWalkTime: this.maxWalkTimeOnBusItinerary(),
      wheelchair: this.wheelcharAccessible(),
      maxWalkDistance: Math.round(this.speedAndTimeToMetersDistance(this.maxWalkTimeOnBusItinerary(), this.walkSpeed().speed)),
      itineraryId: this.itineraryType().id,
      ...this.itineraryType().options,
      walkReluctance: this.wheelcharAccessible() ? 1 : this.itineraryType().options.walkReluctance,
    }
  }

  public setBusDataFromUrlParams(params: busOptionsRequestParameters) {
    this.maxWalkTimeOnBusItinerary.set(params.maxWalkTime);
    const itinerari = ITINERARY_OPTIONS.find((option) => option.id === params.itineraryId) ?? FAST_OPTION;
    itinerari.options.walkReluctance = params.wheelchair ? 1 : itinerari.options.walkReluctance
    this.itineraryType.set(itinerari);
  }

  public getBicycleDataForRequest() {
    return {
      triangleSafetyFactor: this.bicycleAdapted() / 100,
      triangleSlopeFactor: this.flat() / 100,
      triangleTimeFactor: this.fast() / 100,
      optimize: 'TRIANGLE',
    }
  }

  public setBicycleDataFromUrlParams(params: bikeOptionsRequestParameters) {
    this.bicycleAdapted.set(Math.round(params.triangleSafetyFactor * 100));
    this.flat.set(Math.round(params.triangleSlopeFactor * 100));
    this.fast.set(Math.round(params.triangleTimeFactor * 100));
  }

  public getMultimodalDataForRequest() {
    return {
      maxWalkDistance: Math.round(this.speedAndTimeToMetersDistance(this.maxWalkTimeOnMultimodal(), this.bikeSpeed().speed)), // convert to seconds and multiply by walk speed in miles/s
      walkSpeed: this.bikeSpeed().speed,
      maxWalkTime: this.maxWalkTimeOnMultimodal(),
      ...this.itineraryType().options,
      ...this.getBicycleDataForRequest(),
    }
  }

  public setMultimodalDataFromUrlParams(params: allOptionsRequestParameters) {  
    this.maxWalkTimeOnMultimodal.set(params.maxWalkTime);
    this.bikeSpeed.set(BICYCLE_SPEED_OPTIONS.find((option) => option.speed === params.walkSpeed) ?? BICYCLE_NORMAL_SPEED);
  }

  /**
   * 
   * @param time time in minutes
   * @param speed speed in km/h
   * @returns Number of meters that can be walked in the given time at the given speed
   */
  private speedAndTimeToMetersDistance(time: number, speed: number) {
    return  (time * 60) * (speed / 3.6 ) // convert to seconds and multiply by walk speed in m/s
  }
}
